import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  EMPTY,
  Observable,
  catchError,
  from,
  mergeMap,
  throwError,
} from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import {
  SilentRequest,
  AuthenticationResult,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  RedirectRequest,
} from '@azure/msal-browser';
import { MessageService } from 'primeng/api';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private redirectStarted = false;

  constructor(
    private authService: MsalService,
    private messageService: MessageService,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const account = this.authService.instance.getActiveAccount();
    if (!account) {
      // Handle the case where there is no active account
      this.login();
      return EMPTY;
    }

    const requestObj: SilentRequest = {
      account,
      scopes: ['api://b843d78c-a646-42c7-9c7b-7ad8828d160e/API.Access'],
    };

    return from(this.authService.acquireTokenSilent(requestObj)).pipe(
      // Stage 1 - token acquisition: only MSAL errors can come out of here.
      catchError((err: unknown) => {
        if (err instanceof InteractionRequiredAuthError) {
          // If interaction required, redirect to login
          const redirectRequest: RedirectRequest = {
            ...requestObj,
            redirectUri: window.location.origin,
            authority:
              'https://login.microsoftonline.com/6fe48ad2-8b7b-483a-8f25-28fe1a030a74',
          };
          this.startRedirect(() =>
            this.authService.acquireTokenRedirect(redirectRequest),
          );
          return EMPTY; // Return an empty observable so that the HTTP request does not proceed
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Auth Error',
          detail: `Unable to acquire an access token for ${account.username}.`,
          life: 30000,
        });
        console.log(err);
        return throwError(() => err);
      }),
      // Stage 2 - the API request: only HTTP errors can come out of here.
      mergeMap((response: AuthenticationResult) => {
        const authToken = response.accessToken;
        const modifiedRequest = request.clone({
          setHeaders: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        return next.handle(modifiedRequest).pipe(
          catchError((err: unknown) => {
            if (err instanceof HttpErrorResponse && err.status === 401) {
              this.messageService.add({
                severity: 'error',
                summary: 'Auth Error',
                detail: `Unable to authorize api request ${request.url} for ${account.username}.`,
                life: 30000,
              });
            }
            console.log(err);
            // Rethrown on purpose, even after the 401 toast: callers subscribe
            // with `error` handlers that reset their loading state, so the
            // request must still fail instead of completing silently.
            return throwError(() => err);
          }),
        );
      }),
    );
  }

  private login(): void {
    this.startRedirect(() => this.authService.loginRedirect());
  }

  /**
   * Starts an interactive redirect at most once at a time. Several requests can
   * fail with "interaction required" in the same tick (polling, websocket
   * setup...), and MSAL rejects any interactive call made while another one is
   * in progress with `interaction_in_progress`.
   */
  private startRedirect(start: () => Observable<void>): void {
    if (this.redirectStarted) {
      return;
    }
    this.redirectStarted = true;
    start().subscribe({
      error: (error: unknown) => {
        // The page is not navigating away, so allow a later attempt.
        this.redirectStarted = false;
        if (
          !(
            error instanceof BrowserAuthError &&
            error.errorCode === BrowserAuthErrorCodes.interactionInProgress
          )
        ) {
          console.error('Unable to start the login redirect', error);
        }
      },
    });
  }
}
